import { TILE_SIZE } from '../gen/config';
import type { InteriorManager } from './interiorManager';
import type { TileState } from './tileManager';

/** Something the overlay can draw, in generation coordinates (cm). */
export interface OverlayLayer {
  name: string;
  color: string;
  enabled: boolean;
  /** Redrawn every overlay frame; other layers are cached until the view or tile data changes. */
  live?: boolean;
  draw(ctx: CanvasRenderingContext2D, view: OverlayView): void;
}

export interface OverlayView {
  /** Screen x/y for a generation-space point. */
  sx(x: number): number;
  sy(y: number): number;
  /** Pixels per cm. */
  scale: number;
  /** Visible generation-space bounds. */
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Top-down 2D canvas overlay. Follows the camera until dragged; wheel zooms; F re-centres.
 * Layers are registered by the app as each generation phase comes online.
 */
export class DebugOverlay {
  readonly canvas: HTMLCanvasElement;
  private readonly legend: HTMLDivElement;
  private readonly ctx: CanvasRenderingContext2D;
  private visible = false;
  private centerX = 0;
  private centerY = 0;
  /** Pixels per cm. */
  private scale = 0.004;
  private follow = true;
  private dragging = false;
  private lastMouse = { x: 0, y: 0 };
  readonly layers: OverlayLayer[] = [];
  cameraX = 0;
  cameraY = 0;
  cameraYaw = 0;
  /** Called with a generation-space point when the user clicks without dragging. */
  onPick: ((x: number, y: number) => void) | null = null;
  private moved = false;
  /** Static layers (everything not `live`) and tile borders, redrawn only when needed. */
  private readonly staticCanvas = document.createElement('canvas');
  private staticView = '';
  private staticData = -1;
  private staticAt = 0;

  constructor(parent: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'overlay';
    parent.appendChild(this.canvas);
    this.legend = document.createElement('div');
    this.legend.className = 'overlay-legend';
    parent.appendChild(this.legend);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context');
    this.ctx = ctx;

    this.canvas.addEventListener('mousedown', (e) => {
      this.dragging = true;
      this.moved = false;
      this.lastMouse = { x: e.clientX, y: e.clientY };
      e.stopPropagation();
    });
    window.addEventListener('mouseup', (e) => {
      if (this.dragging && !this.moved && e.target === this.canvas) {
        const r = this.canvas.getBoundingClientRect();
        const dpr = this.canvas.width / r.width;
        const px = (e.clientX - r.left) * dpr;
        const py = (e.clientY - r.top) * dpr;
        const gx = this.centerX + (px - this.canvas.width / 2) / this.scale;
        const gy = this.centerY - (py - this.canvas.height / 2) / this.scale;
        this.onPick?.(gx, gy);
      }
      this.dragging = false;
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.lastMouse.x;
      const dy = e.clientY - this.lastMouse.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) this.moved = true;
      if (!this.moved) return;
      const r = this.canvas.getBoundingClientRect();
      const dpr = this.canvas.width / r.width;
      this.follow = false;
      this.centerX -= (dx * dpr) / this.scale;
      this.centerY += (dy * dpr) / this.scale;
      this.lastMouse = { x: e.clientX, y: e.clientY };
    });
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scale *= e.deltaY < 0 ? 1.2 : 1 / 1.2;
        this.scale = Math.max(0.0002, Math.min(0.5, this.scale));
      },
      { passive: false },
    );
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyF' && this.visible) this.follow = true;
    });
  }

  get shown(): boolean {
    return this.visible;
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.canvas.classList.toggle('visible', v);
    this.legend.classList.toggle('visible', v);
    this.rebuildLegend();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  addLayer(layer: OverlayLayer): void {
    this.layers.push(layer);
    this.rebuildLegend();
  }

  private readonly checks = new Map<OverlayLayer, HTMLInputElement>();

  private rebuildLegend(): void {
    this.legend.innerHTML = '';
    this.checks.clear();
    for (const l of this.layers) {
      const span = document.createElement('span');
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = l.enabled;
      cb.onchange = () => (l.enabled = cb.checked);
      this.checks.set(l, cb);
      const sw = document.createElement('i');
      sw.style.background = l.color;
      span.append(cb, sw, l.name);
      this.legend.appendChild(span);
    }
    const tip = document.createElement('span');
    tip.textContent = 'F: follow camera';
    this.legend.appendChild(tip);
  }

  /**
   * Static layers are drawn into an offscreen canvas that's reused until the view changes (zoom, drag,
   * resize, layer toggles, a follow re-centre) or tile data changes (at most every 0.5 s). Following
   * the camera re-centres in steps once it is 30% of the way to the edge, not every frame: redrawing
   * every loaded tile's slope cells, blocks and roads on each tick stalled the frame (DECISIONS).
   */
  draw(tiles: Iterable<TileState>, dataVersion = 0): void {
    if (!this.visible) return;
    const c = this.canvas;
    const r = c.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.round(r.width * dpr);
    const h = Math.round(r.height * dpr);
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    const s = this.scale;
    if (this.follow && (Math.abs(this.cameraX - this.centerX) * s > 0.3 * (w / 2) || Math.abs(this.cameraY - this.centerY) * s > 0.3 * (h / 2))) {
      this.centerX = this.cameraX;
      this.centerY = this.cameraY;
    }
    const ctx = this.ctx;
    const cx = this.centerX;
    const cy = this.centerY;
    const view: OverlayView = {
      sx: (x) => (x - cx) * s + w / 2,
      sy: (y) => h / 2 - (y - cy) * s,
      scale: s,
      minX: cx - w / 2 / s,
      maxX: cx + w / 2 / s,
      minY: cy - h / 2 / s,
      maxY: cy + h / 2 / s,
    };

    for (const l of this.layers) {
      const cb = this.checks.get(l);
      if (cb && cb.checked !== l.enabled) cb.checked = l.enabled;
    }
    const mask = this.layers.map((l) => (l.enabled ? 1 : 0)).join('');
    const viewKey = `${cx}|${cy}|${s}|${w}x${h}|${mask}`;
    const now = performance.now();
    if (viewKey !== this.staticView || (dataVersion !== this.staticData && now - this.staticAt > 500)) {
      this.staticView = viewKey;
      this.staticData = dataVersion;
      this.staticAt = now;
      this.drawStatic(tiles, view, w, h, dpr);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.staticCanvas, 0, 0);
    for (const l of this.layers) if (l.enabled && l.live) l.draw(ctx, view);

    // Camera marker.
    const px = view.sx(this.cameraX);
    const py = view.sy(this.cameraY);
    ctx.fillStyle = '#ffcc00';
    ctx.beginPath();
    ctx.arc(px, py, 4 * dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffcc00';
    ctx.beginPath();
    ctx.moveTo(px, py);
    // yaw 0 looks along three -z = generation +y (screen up).
    ctx.lineTo(px - Math.sin(this.cameraYaw) * 18 * dpr, py - Math.cos(this.cameraYaw) * 18 * dpr);
    ctx.stroke();
    // Scale bar (100 m).
    const bar = 10000 * s;
    ctx.fillStyle = '#8b949e';
    ctx.font = `${Math.round(10 * dpr)}px monospace`;
    ctx.fillRect(10, h - 14, bar, 3);
    ctx.fillText('100 m', 10, h - 18);
  }

  private drawStatic(tiles: Iterable<TileState>, view: OverlayView, w: number, h: number, dpr: number): void {
    const c = this.staticCanvas;
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    const ctx = c.getContext('2d')!;
    const s = view.scale;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, w, h);
    for (const l of this.layers) if (l.enabled && !l.live) l.draw(ctx, view);

    // Tile borders + labels.
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = `${Math.round(10 * dpr)}px monospace`;
    for (const t of tiles) {
      const x0 = t.tx * TILE_SIZE - TILE_SIZE / 2;
      const y0 = t.ty * TILE_SIZE - TILE_SIZE / 2;
      if (x0 > view.maxX || x0 + TILE_SIZE < view.minX || y0 > view.maxY || y0 + TILE_SIZE < view.minY) continue;
      const a = view.sx(x0);
      const b = view.sy(y0 + TILE_SIZE);
      ctx.strokeRect(a, b, TILE_SIZE * s, TILE_SIZE * s);
      if (TILE_SIZE * s > 60) ctx.fillText(`${t.tx},${t.ty} L${t.shownLod}`, a + 3, b + 12 * dpr);
    }
  }
}

/** Tile square intersects the visible view (with a one-tile margin for blocks spanning borders). */
export function tileVisible(t: TileState, v: OverlayView, margin = TILE_SIZE): boolean {
  const x0 = t.tx * TILE_SIZE - TILE_SIZE / 2 - margin;
  const y0 = t.ty * TILE_SIZE - TILE_SIZE / 2 - margin;
  const x1 = x0 + TILE_SIZE + 2 * margin;
  const y1 = y0 + TILE_SIZE + 2 * margin;
  return x1 >= v.minX && x0 <= v.maxX && y1 >= v.minY && y0 <= v.maxY;
}

/** Buildable-region layer: LOD1 slope cells, coloured by group. */
export function buildableLayer(getTiles: () => Iterable<TileState>): OverlayLayer {
  const palette = ['#2e7d32', '#1565c0', '#6a1b9a', '#00838f', '#ef6c00', '#ad1457', '#558b2f', '#4527a0'];
  return {
    name: 'buildable',
    color: '#2e7d32',
    enabled: true,
    draw(ctx, v) {
      for (const t of getTiles()) {
        const a = t.analysis;
        if (!a || !tileVisible(t, v, 0)) continue;
        const cell = a.step * v.scale;
        const x0 = v.sx(t.tx * TILE_SIZE - TILE_SIZE / 2);
        const y0 = v.sy(t.ty * TILE_SIZE + TILE_SIZE / 2);
        ctx.save();
        ctx.beginPath();
        ctx.rect(x0, y0, TILE_SIZE * v.scale, TILE_SIZE * v.scale);
        ctx.clip();
        for (let y = a.lo; y <= a.hi; y++) {
          for (let x = a.lo; x <= a.hi; x++) {
            const g = a.groups[y * a.n + x];
            const wx = a.originX + x * a.step;
            const wy = a.originY + y * a.step;
            if (wx < v.minX - a.step || wx > v.maxX + a.step || wy < v.minY - a.step || wy > v.maxY + a.step) continue;
            if (g > 0) {
              ctx.fillStyle = palette[(g - 1) % palette.length] + '55';
            } else {
              const s = a.slopes[y * a.n + x];
              ctx.fillStyle = s >= 100 ? 'rgba(120,60,60,0.35)' : 'rgba(90,80,70,0.35)';
            }
            ctx.fillRect(v.sx(wx) - cell / 2, v.sy(wy) - cell / 2, cell, cell);
          }
        }
        ctx.restore();
      }
    },
  };
}

/** Road graph: edges (main amber, secondary white), nodes, gates (magenta). */
export function roadLayer(getTiles: () => Iterable<TileState>): OverlayLayer {
  return {
    name: 'road graph',
    color: '#ffb000',
    enabled: true,
    draw(ctx, v) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      for (const t of getTiles()) {
        const r = t.roads;
        if (!r || !tileVisible(t, v, 0)) continue;
        const g = r.graph;
        const ne = g.edges.length / 2;
        for (let i = 0; i < ne; i++) {
          const a = g.edges[i * 2];
          const b = g.edges[i * 2 + 1];
          ctx.strokeStyle = g.types[i] === 0 ? '#ffb000' : '#e6edf3';
          ctx.lineWidth = (g.types[i] === 0 ? 2.5 : 1.5) * dpr;
          ctx.beginPath();
          ctx.moveTo(v.sx(g.nodes[a * 2]), v.sy(g.nodes[a * 2 + 1]));
          ctx.lineTo(v.sx(g.nodes[b * 2]), v.sy(g.nodes[b * 2 + 1]));
          ctx.stroke();
        }
        if (v.scale > 0.002) {
          ctx.fillStyle = '#58a6ff';
          for (let i = 0; i < g.nodes.length; i += 2) ctx.fillRect(v.sx(g.nodes[i]) - 1.5 * dpr, v.sy(g.nodes[i + 1]) - 1.5 * dpr, 3 * dpr, 3 * dpr);
        }
        ctx.fillStyle = '#ff4fd8';
        for (const gt of r.gates) ctx.fillRect(v.sx(gt.x) - 3 * dpr, v.sy(gt.y) - 3 * dpr, 6 * dpr, 6 * dpr);
      }
    },
  };
}

/** Rejected road candidates, coloured by reason. */
export function rejectedLayer(getTiles: () => Iterable<TileState>): OverlayLayer {
  const colors: Record<string, string> = {
    outside: 'rgba(160,40,40,0.8)',
    unbuildable: 'rgba(255,120,0,0.8)',
    crossing: 'rgba(255,80,160,0.8)',
    discarded: 'rgba(170,90,255,0.8)',
    degenerate: 'rgba(120,120,120,0.8)',
  };
  return {
    name: 'rejected candidates',
    color: '#ff5050',
    enabled: true,
    draw(ctx, v) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      ctx.lineWidth = 1 * dpr;
      ctx.setLineDash([4 * dpr, 3 * dpr]);
      for (const t of getTiles()) {
        if (!t.roads || !tileVisible(t, v, 0)) continue;
        for (const r of t.roads.rejected) {
          ctx.strokeStyle = colors[r.reason] ?? '#f00';
          ctx.beginPath();
          ctx.moveTo(v.sx(r.start.x), v.sy(r.start.y));
          ctx.lineTo(v.sx(r.end.x), v.sy(r.end.y));
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);
    },
  };
}

function pathFlat(ctx: CanvasRenderingContext2D, v: OverlayView, flat: number[]): void {
  ctx.beginPath();
  for (let i = 0; i < flat.length; i += 2) {
    const x = v.sx(flat[i]);
    const y = v.sy(flat[i + 1]);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Block faces (fill tinted per owner tile), cleaned outline, and sidewalk line. */
export function blockLayer(getTiles: () => Iterable<TileState>): OverlayLayer {
  return {
    name: 'blocks',
    color: '#3fb950',
    enabled: true,
    draw(ctx, v) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      for (const t of getTiles()) {
        if (!t.blocks || !tileVisible(t, v)) continue;
        const hue = (((t.tx * 73856093) ^ (t.ty * 19349663)) >>> 0) % 360;
        for (const b of t.blocks) {
          pathFlat(ctx, v, b.face);
          ctx.fillStyle = `hsla(${hue},55%,50%,0.22)`;
          ctx.fill();
          ctx.strokeStyle = `hsla(${hue},70%,65%,0.9)`;
          ctx.lineWidth = 1 * dpr;
          pathFlat(ctx, v, b.polygon);
          ctx.stroke();
          if (b.sidewalk.length >= 6 && v.scale > 0.003) {
            ctx.setLineDash([3 * dpr, 3 * dpr]);
            ctx.strokeStyle = 'rgba(200,200,200,0.6)';
            pathFlat(ctx, v, b.sidewalk);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }
    },
  };
}

/** Building footprints: towers (ground plot + top floor dashed), low-rise units, parks. */
export function footprintLayer(getTiles: () => Iterable<TileState>): OverlayLayer {
  return {
    name: 'footprints',
    color: '#d2a8ff',
    enabled: true,
    draw(ctx, v) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      for (const t of getTiles()) {
        if (!t.buildings || !tileVisible(t, v)) continue;
        for (const b of t.buildings) {
          if (b.kind === 'park') {
            pathFlat(ctx, v, b.footprint);
            ctx.fillStyle = 'rgba(80,160,70,0.45)';
            ctx.fill();
            continue;
          }
          if (b.kind === 'lowrise') {
            for (const u of b.parts) {
              pathFlat(ctx, v, u);
              ctx.fillStyle = 'rgba(210,150,110,0.55)';
              ctx.fill();
              ctx.strokeStyle = 'rgba(255,200,160,0.9)';
              ctx.lineWidth = 1 * dpr;
              ctx.stroke();
            }
            continue;
          }
          pathFlat(ctx, v, b.footprint);
          ctx.fillStyle = 'rgba(210,168,255,0.35)';
          ctx.fill();
          ctx.strokeStyle = '#d2a8ff';
          ctx.lineWidth = 1.5 * dpr;
          ctx.stroke();
          for (const p of b.parts) {
            ctx.setLineDash([2 * dpr, 2 * dpr]);
            pathFlat(ctx, v, p);
            ctx.stroke();
            ctx.setLineDash([]);
          }
          if (v.scale > 0.004) {
            ctx.fillStyle = '#fff';
            ctx.fillText(`${b.floors}F`, v.sx(b.center.x) - 8, v.sy(b.center.y) + 4);
          }
        }
      }
    },
  };
}


function holeyPath(ctx: CanvasRenderingContext2D, v: OverlayView, outer: number[], holes: number[][]): void {
  ctx.beginPath();
  for (const ring of [outer, ...holes]) {
    for (let i = 0; i < ring.length; i += 2) {
      const x = v.sx(ring[i]);
      const y = v.sy(ring[i + 1]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }
}

/**
 * Interior floors: room outlines and doors for every active floor; for the selected room its floor
 * polygon, obstacles, walking zones, walkable (eroded) region and rejected placements.
 */
export function interiorLayer(im: InteriorManager): OverlayLayer {
  return {
    name: 'interiors (click a room)',
    color: '#79c0ff',
    enabled: true,
    live: true,
    draw(ctx, v) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      for (const f of im.floors.values()) {
        f.result.rooms.forEach((r, i) => {
          const sel = im.selected?.floorKey === f.key && im.selected.room === i;
          pathFlat(ctx, v, r.poly);
          ctx.strokeStyle = sel ? '#ffffff' : r.type === 'hallway' ? 'rgba(121,192,255,0.9)' : r.type === 'elevator' ? 'rgba(255,120,120,0.9)' : 'rgba(121,192,255,0.5)';
          ctx.lineWidth = (sel ? 2 : 1) * dpr;
          ctx.stroke();
        });
        ctx.fillStyle = '#ffd33d';
        for (const d of f.result.doors) ctx.fillRect(v.sx(d.x) - 2 * dpr, v.sy(d.y) - 2 * dpr, 4 * dpr, 4 * dpr);
      }
      const s = im.selected;
      if (!s) return;
      const f = im.floors.get(s.floorKey);
      const r = f?.result.rooms[s.room];
      if (!r) return;
      const d = r.debug;
      if (d) {
        for (const w of d.walkable) {
          holeyPath(ctx, v, w.outer, w.holes);
          ctx.fillStyle = 'rgba(63,185,80,0.35)';
          ctx.fill('evenodd');
          ctx.strokeStyle = 'rgba(63,185,80,0.9)';
          ctx.lineWidth = 1 * dpr;
          ctx.stroke();
        }
        pathFlat(ctx, v, d.inner);
        ctx.strokeStyle = '#ffffff';
        ctx.setLineDash([4 * dpr, 2 * dpr]);
        ctx.stroke();
        ctx.setLineDash([2 * dpr, 2 * dpr]);
        ctx.strokeStyle = 'rgba(0,220,255,0.9)';
        for (const w of d.walking) {
          pathFlat(ctx, v, w);
          ctx.stroke();
        }
        ctx.strokeStyle = 'rgba(255,80,80,0.8)';
        for (const rj of d.rejected) {
          pathFlat(ctx, v, rj.outline);
          ctx.stroke();
        }
        ctx.setLineDash([]);
      }
      for (const o of r.objects) {
        pathFlat(ctx, v, o.outline);
        ctx.fillStyle = o.kind === 'ceiling' ? 'rgba(255,255,200,0.25)' : o.kind === 'on-wall' ? 'rgba(200,150,255,0.6)' : 'rgba(255,150,60,0.65)';
        ctx.fill();
      }
    },
  };
}
